import InfoIcon from "@mui/icons-material/Info";
import { Box, IconButton, Tooltip } from "@mui/material";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateBarcodeField } from "features/WorklistTools/shared/components/PlateBarcodeField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import { useCtrlDown } from "features/WorklistTools/shared/hooks/hooks";
import {
  PlatePosition,
  ReactSelectableFast,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import { SelectableGroup } from "react-selectable-fast";
import styled from "styled-components";
import { getIntTotalVol } from "../NucleofectionToolHelpers";
import { IntermediatePlateInfoState, NucleofectionToolActions } from "../state";
import { NucleofectionInternalAction } from "../state/action";
import { useNucleofectionStateProvider } from "../state/StateProvider";
import { IntermediatePlate } from "./IntermediatePlate";

const StyledSelectableGroup = styled(SelectableGroup)`
  height: 320px;
  width: 100%;
`;

const StyledPlateSettingsContainer = styled.div`
  margin-top: 10px;
`;

const StyledIntPlatesContainer = styled.div`
  height: calc(100vh - 300px);
  width: 600px;
  overflow: auto;
`;

export const SelectableIntPlatesContainer = (intPlateTypeInfo: any) => {
  const { nucleofectionInternalState, nucleofectionDispatch } =
    useNucleofectionStateProvider();

  const dispatch = useAppReduxDispatch();
  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.sourcePlateInfo
  );
  const nucPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.nucPlateInfo
  );
  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.worklistValues
  );
  const intPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.intPlateInfo
  );
  const handleIntermediateWellSelection = (
    selection: ReactSelectableFast[]
  ) => {
    const wellSelection = selection.map((well) => ({
      plateIndex: well.props.plateIndex,
      plateWellId: well.props.plateWellId,
      isSelectable: !well.node.classList.contains("not-selectable"),
    }));
    nucleofectionDispatch({
      type: NucleofectionInternalAction.SET_SELECTED_INTERMEDIATE_WELLS,
      payload: {
        selectedIntermediateWells: wellSelection,
      },
    });
  };

  const handleIntPlateInfoChanged = (
    index: number,
    key: string,
    value: any
  ) => {
    dispatch(
      NucleofectionToolActions.UPDATE_INT_PLATE_INFO({
        index,
        key,
        value,
      })
    );
  };

  const handleWellHover = (wellId: string) => {
    nucleofectionDispatch({
      type: NucleofectionInternalAction.SET_HOVER_WELL_ID,
      payload: {
        hoverWellId: wellId,
      },
    });
  };

  const ctrlDown = useCtrlDown();

  const intTotalVol = getIntTotalVol(
    nucPlateInfo[0].topUpVol,
    nucPlateInfo[0].washVol,
    nucPlateInfo[0].numberOfWashes
  );

  return (
    <StyledIntPlatesContainer>
      {intPlateInfo.map((item: IntermediatePlateInfoState, index: number) => (
        <div style={{ padding: "20px" }}>
          <PlateBarcodeField
            plateRole={PlatePosition.Intermediate}
            index={index}
            label={`Intermediate Plate Barcode`}
            plateBarcode={item.plateBarcode}
            handlePlateBarcodeChange={(index, field, value) =>
              handleIntPlateInfoChanged(index!, field, value)
            }
            disabled={item.plateBarcode === ""}
          />
          <div>
            <StyledSelectableGroup
              onSelectionFinish={handleIntermediateWellSelection}
              resetOnStart={
                ctrlDown.worklistToolInternalState.setCtrlDown ? false : true
              }
              allowCtrlClick={true}
            >
              <IntermediatePlate
                worklistTool={WorklistTools.Nucleofection}
                index={index}
                plateRows={item.rows}
                plateCols={item.cols}
                wellMapping={worklistValues.nucToInt}
                nucPlateInfo={nucPlateInfo}
                // sourcePlatePosition={PlatePosition.Nucleofection}
                handleWellHover={handleWellHover}
              />
            </StyledSelectableGroup>
          </div>
          <StyledPlateSettingsContainer>
            <PlateType
              platePosition={PlatePosition.Intermediate}
              isDisabled={
                nucleofectionInternalState.methodSettings!.selectedSystem ===
                  0 || nucPlateInfo[index].rows.length === 0
              }
              acceptedPlateTypes={intPlateTypeInfo.intPlateInfo}
              plateType={item.labwareTypeCode}
              setPlateType={(plateTypeInfo) =>
                handleIntPlateInfoChanged(
                  index,
                  "labwareTypeCode",
                  plateTypeInfo
                )
              }
            />
          </StyledPlateSettingsContainer>
          {item.rows.length > 0 ? (
            <div style={{ display: "flex", width: "100%" }}>
              <NumberField
                style={{ width: "100%", marginTop: "10px", marginRight: "5px" }}
                id="operating-vol"
                label="Operating Vol (µL)"
                value={item.operatingVol}
                onChange={(value) =>
                  handleIntPlateInfoChanged(
                    index,
                    "operatingVol",
                    parseInt(value)
                  )
                }
                disabled={true}
              />
              <NumberField
                style={{
                  width: "100%",
                  marginTop: "10px",
                  marginRight: "5px",
                }}
                id="total-vol"
                label={
                  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                    Total Vol (µL)
                    <Tooltip
                      arrow
                      placement="top"
                      title="(Nucleofection Top Up Vol) + (Wash Cycles x Wash Vol)"
                    >
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                value={intTotalVol}
                onChange={(value) =>
                  handleIntPlateInfoChanged(index, "totalVol", parseInt(value))
                }
                disabled={true}
              />
            </div>
          ) : null}
        </div>
      ))}
    </StyledIntPlatesContainer>
  );
};
